import { CopilotChart } from 'apps/sigma2-ui/src/components/CopilotChart';
import { PromotionCopilotCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/PromotionCopilotDefinition';
import { PromotionCopilotRequest } from 'libs/end-to-end/src/custom-viz/PromotionCopilotTypes';
import { CopilotSlateView } from 'apps/sigma2-ui/src/components/CopilotSlateView';
import React, { useCallback, useMemo, useState } from 'react';
import { usePromotionCopilotUpdateDropdownsEffect } from 'apps/sigma2-ui/src/copilot/hooks/usePromotionCopilotUpdateDropdownsEffect';
import { usePromotionCopilotCurrentCustomVizRequestToExecute } from 'apps/sigma2-ui/src/copilot/hooks/usePromotionCopilotCurrentCustomVizRequestToExecute';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { useBasicCopilotAddToSlate } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotAddToSlate';
import { useBasicCopilotRemoveFromSlate } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotRemoveFromSlate';
import { useBasicCopilotCurrentState } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotCurrentState';
import { useBasicCopilotSubjectsWithSlateStatus } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotSubjectsWithSlateStatus';
import { useBasicCopilotSlateCopilotDataPoints } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotSlateCopilotDataPoints';
import { useBasicCopilotDepartmentsAndLocations } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotDepartmentsAndLocations';
import { useBasicCopilotDeptToRolesMap } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotDeptToRolesMap';
import { useBasicCopilotApplicableRoles } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotApplicableRoles';
import { useBasicCopilotData } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotData';
import { useBasicCopilotSubjectIdsToPrefill } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotSubjectIdsToPrefill';
import { useBasicCopilotPrefillSubjectIdsEffect } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotPrefillSubjectIdsEffect';
import { useBasicCopilotOnFetchShareLink } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotOnFetchShareLink';
import { usePromotionCopilotCanAnalyze } from 'apps/sigma2-ui/src/copilot/hooks/usePromotionCopilotCanAnalyze';
import { usePromotionCopilotLastPersistedRequest } from 'apps/sigma2-ui/src/copilot/hooks/usePromotionCopilotLastPersistedRequest';
import { HiringAndPromotionCopilotWrapper } from 'apps/sigma2-ui/src/copilot/components/HiringAndPromotionCopilotWrapper';
import { useCopilotCustomVizAutoloader } from 'apps/sigma2-ui/src/lib/hooks/useCopilotCustomVizAutoloader';
import { MyObservation } from 'libs/end-to-end/src/my-observation';

export const PromotionCopilotView = ({
  organizationId,
  myObservation,
}: {
  myObservation: MyObservation;
  organizationId: string;
}) => {
  const { userAuthenticatedApiCall } = useUser();

  const { getLevelByColumnValue } = useDimensionConfig();

  const context = useCustomViz();

  const {
    result,
    status,
    dropdownOptions,
    doViz,
    lastSuccessfulCustomVizRequest,
  } = useMemo(() => {
    return context;
  }, [context]);

  const [selectedSlateIds, setSelectedSlateIds] = useState<string[]>([]);

  const [currentShareLink, setCurrentShareLink] = useState<string | null>(null);

  const [showShareLinkModal, setShowShareLinkModal] = useState(false);

  const [showGraphTooltip, setShowGraphTooltip] = useState(false);

  const [nextSubjectIdToScrollTo, setNextSubjectIdToScrollTo] = useState<
    string | null
  >(null);

  const [currentlySelectedSubjectId, setCurrentlySelectedSubjectId] = useState<
    string | null
  >(null);

  const [selectedDepartments, internalSetSelectedDepartments] = useState<
    string[]
  >([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  const [isFetchingShareLink, setIsFetchingShareLink] = useState(false);

  const addToSlate = useBasicCopilotAddToSlate({
    setSelectedSlateIds,
  });

  const removeFromSlate = useBasicCopilotRemoveFromSlate({
    setSelectedSlateIds,
  });

  const currentState = useBasicCopilotCurrentState({
    status,
    result,
  });

  const subjectsWithSlateStatus = useBasicCopilotSubjectsWithSlateStatus({
    selectedSlateIds,
    currentState,
    getLevelByColumnValue,
  });

  const slateCopilotDataPoints = useBasicCopilotSlateCopilotDataPoints({
    currentState,
    selectedSlateIds,
    getLevelByColumnValue,
  });

  const { departments, locations } = useBasicCopilotDepartmentsAndLocations({
    dropdownOptions,
  });

  const deptToRolesMap = useBasicCopilotDeptToRolesMap({
    dropdownOptions,
  });

  const { setSelectedDepartmentsWithRoles, currentApplicableRoles } =
    useBasicCopilotApplicableRoles({
      deptToRolesMap,
      setSelectedDepartments: internalSetSelectedDepartments,
      selectedDepartments,
      setSelectedRoles,
    });

  const canAnalyze = usePromotionCopilotCanAnalyze({
    selectedLocations,
    status,
    selectedRoles,
    selectedDepartments,
  });

  const [shouldPrefillFilters, setShouldPrefillFilters] = useState(false);

  const resetFiltersToInitialState = usePromotionCopilotUpdateDropdownsEffect({
    setSelectedDepartments: internalSetSelectedDepartments,
    setSelectedRoles,
    setSelectedLocations,
    shouldPrefillFilters,
  });

  const currentCustomVizRequestToExecute =
    usePromotionCopilotCurrentCustomVizRequestToExecute({
      selectedRoles,
      selectedDepartments,
      selectedLocations,
      observation: myObservation.type,
      organizationId,
    });

  const { lastPersistedRequest } = usePromotionCopilotLastPersistedRequest({
    myObservation,
    lastSuccessfulCustomVizRequest,
  });

  const copilotData = useBasicCopilotData({
    result,
  });

  const subjectIdsToPrefill = useBasicCopilotSubjectIdsToPrefill({
    copilotData,
  });

  useBasicCopilotPrefillSubjectIdsEffect({
    copilotData,
    setSelectedSlateIds,
    subjectIdsToPrefill,
  });

  const onFetchShareLink = useBasicCopilotOnFetchShareLink({
    request: currentCustomVizRequestToExecute,
    setShowShareLinkModal,
    setCurrentShareLink,
    userAuthenticatedApiCall,
    setIsFetchingShareLink,
    isFetchingShareLink,
  });

  const setSelections = useCallback((value: PromotionCopilotRequest) => {
    internalSetSelectedDepartments(value.inputs.dropdowns.department);
    setSelectedRoles(value.inputs.dropdowns.job_title);
    setSelectedLocations(value.inputs.dropdowns.location);
  }, []);

  const onAnalyse = useCallback(() => {
    if (!currentCustomVizRequestToExecute) {
      return;
    }

    doViz(currentCustomVizRequestToExecute);
  }, [currentCustomVizRequestToExecute, doViz]);

  const onAnalyseWithRequest = useCallback(
    (request: PromotionCopilotRequest) => {
      doViz(request);
    },
    [doViz],
  );

  const { onUserAnalysisCancel } = useCopilotCustomVizAutoloader({
    canAnalyze,
    onAnalyze: onAnalyse,
    onAnalyseWithRequest,
    context,
    lastPersistedRequest,
    setSelections,
    setShouldPrefillFilters,
    shouldPrefillFilters,
    type: PromotionCopilotRequest,
    resetFiltersToInitialState,
    resetMetricsToInitialState: null,
    resetDimensionsToInitialState: null,
  });

  return (
    <HiringAndPromotionCopilotWrapper
      chart={
        <CopilotChart
          subjectsWithSlateStatus={subjectsWithSlateStatus}
          addToSlate={addToSlate}
          removeFromSlate={removeFromSlate}
          currentlySelectedSubjectId={currentlySelectedSubjectId}
          setCurrentlySelectedSubjectId={setCurrentlySelectedSubjectId}
          setShowGraphTooltip={setShowGraphTooltip}
          applicableRoles={currentApplicableRoles}
          locations={locations}
          selectedDepartments={selectedDepartments}
          selectedRoles={selectedRoles}
          selectedLocations={selectedLocations}
          setSelectedDepartments={setSelectedDepartmentsWithRoles}
          setSelectedRoles={setSelectedRoles}
          setSelectedLocations={setSelectedLocations}
          selectedStartDate={undefined}
          selectedEndDate={undefined}
          focusArea={'Promotion'}
          slateCopilotDataPoints={slateCopilotDataPoints}
          showShareLinkModal={showShareLinkModal}
          showGraphTooltip={showGraphTooltip}
          setShowShareLinkModal={setShowShareLinkModal}
          setNextSubjectIdToScrollTo={setNextSubjectIdToScrollTo}
          departments={departments}
          currentShareLink={currentShareLink}
          currentCustomVizRequestToExecute={currentCustomVizRequestToExecute}
          canAnalyze={canAnalyze}
          setSelectedEndDate={() => {}}
          setSelectedStartDate={() => {}}
          onUserAnalysisCancel={onUserAnalysisCancel}
        />
      }
      slate={
        <CopilotSlateView
          focusArea={'Promotion'}
          definition={PromotionCopilotCustomVizDefinition}
          setShowGraphTooltip={setShowGraphTooltip}
          setCurrentlySelectedSubjectId={setCurrentlySelectedSubjectId}
          currentlySelectedSubjectId={currentlySelectedSubjectId}
          removeFromSlate={removeFromSlate}
          addToSlate={addToSlate}
          subjectsWithSlateStatus={subjectsWithSlateStatus}
          subjectIdsToPrefill={subjectIdsToPrefill}
          selectedSlateIds={selectedSlateIds}
          nextSubjectIdToScrollTo={nextSubjectIdToScrollTo}
          isFetchingShareLink={isFetchingShareLink}
          onFetchShareLink={onFetchShareLink}
          isShareable={!!currentCustomVizRequestToExecute}
          setSelectedSlateIds={setSelectedSlateIds}
        />
      }
    />
  );
};
